import { Box, Typography } from '@mui/material'
import { CSSProperties, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import KumocanButton from 'components/button'
import { assignApi } from 'hooks/api/assign'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import NotificationFilesComponent from 'pages/notification/components/notificationFiles'
import { AllianceAssign, Assign, ASSIGN_STATUS } from 'types/assign'
import { NotificationFileInfo } from 'types/notification'
import { OrderWithAssign } from 'types/order'
import { BLUE_COLOR, PRIMARY_COLOR } from 'valiable'

// NOTE 現場詳細ページ
export default function AssignDetailPage() {
  const { t } = useTranslation(['page-assign-detail', 'common'])
  const { showSnackbar } = useSnackbar()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')

  const [totalVolume, setTotalVolume] = useState<number>()
  const [qualifiedVolume, setQualifiedVolume] = useState<number>()
  const [nonQualifiedVolume, setNonQualifiedVolume] = useState<number>()
  const [order, setOrder] = useState<OrderWithAssign>()
  const [otherAssigns, setOtherAssigns] = useState<Assign[]>([])
  const [otherAllianceAssigns, setOtherAllianceAssigns] = useState<
    AllianceAssign[]
  >([])
  const [notificationFiles, setNotificationFiles] = useState<
    NotificationFileInfo[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const fetchData = useCallback(() => {
    if (!id) return

    setIsLoading(true)
    assignApi()
      .findDetailById(id)
      .then((res) => {
        setTotalVolume(res.data.volume)
        setQualifiedVolume(res.data.qualifiedVolume)
        setNonQualifiedVolume(res.data.nonQualifiedVolume)
        setOrder(res.data.order)
        setOtherAssigns(res.data.otherAssigns)
        setOtherAllianceAssigns(res.data.otherAllianceAssigns)
        setNotificationFiles(res.data.notificationFiles)
      })
      .catch((err) => {
        console.error('Error fetching data:', err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (isLoading) {
    return (
      <Box sx={{ width: '90%', margin: '20px auto' }}>
        <Typography sx={{ fontSize: '20px', textAlign: 'center' }}>
          {t('common:loading')}
        </Typography>
      </Box>
    )
  }

  if (!order) {
    return (
      <Box sx={{ width: '90%', margin: '20px auto' }}>
        <Typography sx={{ fontSize: '20px', textAlign: 'center' }}>
          {t('common:not_found', { name: t('order_info') })}
        </Typography>
      </Box>
    )
  }

  // 配置済みの人員を分類
  const qualifiedAssigns = otherAssigns
    .filter((a) => a.status !== ASSIGN_STATUS.DELETED && !!a.hasQualified)
    .sort((a, b) => Number(b.isLeader) - Number(a.isLeader))
  const nonQualifiedAssigns = otherAssigns
    .filter((a) => a.status !== ASSIGN_STATUS.DELETED && !a.hasQualified)
    .sort((a, b) => Number(b.isLeader) - Number(a.isLeader))

  // 配置済みの協力会社を分類
  const qualifiedAllianceAssigns = otherAllianceAssigns.filter(
    (a) => !!a.hasQualified,
  )
  const nonQualifiedAllianceAssigns = otherAllianceAssigns.filter(
    (a) => !a.hasQualified,
  )

  // 配置予定人数に対して未確定人数を計算
  const qualifiedPendingCount = Math.max(
    (qualifiedVolume || 0) -
      qualifiedAssigns.length -
      qualifiedAllianceAssigns.length,
    0,
  )
  const nonQualifiedPendingCount = Math.max(
    (nonQualifiedVolume || 0) -
      nonQualifiedAssigns.length -
      nonQualifiedAllianceAssigns.length,
    0,
  )

  const toMap = () => {
    if (!order?.address) {
      showSnackbar(t('common:messages.address_not_registered'))
    } else if (order?.lat !== 0 && order?.lng !== 0) {
      location.href = `https://www.google.com/maps/search/?api=1&query=${order?.lat},${order?.lng}&launch_app=true`
    } else {
      location.href = `https://www.google.com/maps/search/?api=1&query=${order?.address}&launch_app=true`
    }
  }

  return (
    <Box sx={{ paddingBottom: '300px' }}>
      <Box>
        <Box sx={cardStyle}>
          <>
            <div style={dateHeaderStyle}>
              <p style={dateBoldTextStyle}>
                {order.date!.format(t('common:date_format.date_with_day'))}{' '}
                {order.startTime?.format('HH:mm')}~
                {order.endTime?.format('HH:mm')}
              </p>
              {order.assign.loanInfo?.lendCompanyId && (
                <p style={loanInfoBadgeStyle}>
                  {order.assign.loanInfo.borrowCompanyName}/
                  {order.assign.loanInfo.borrowCompanyBranchName}
                </p>
              )}
            </div>

            <div style={columnDivStyle}>
              <p style={labelTextStyle}>{t('client_name')}</p>
              <p>{order.clientName}</p>
            </div>

            <div style={columnDivStyle}>
              <p style={labelTextStyle}>{t('contract_name')}</p>
              <p>{order.contractDetailName}</p>
            </div>

            <div style={columnDivStyle}>
              <p style={labelTextStyle}>{t('nearest_station')}</p>
              <p>{order.nearestStation}</p>
            </div>

            <div style={columnDivStyle}>
              <p style={labelTextStyle}>{t('address')}:</p>
              <div>
                <p>{order.address}</p>
                <div style={mapButtonContainerStyle}>
                  <KumocanButton onClick={toMap}>
                    {t('common:buttons.map')}
                  </KumocanButton>
                </div>
              </div>
            </div>

            <div style={columnDivStyle}>
              <p style={labelTextStyle}>{t('assigned_count')}</p>
              <p>{t('people_count', { count: totalVolume })}</p>
            </div>

            <div style={columnDivStyle}>
              <p style={labelTextStyle}>{t('workers')}</p>
              <div style={workersContainerStyle}>
                <div>
                  <p style={hasQualifiedStyle}>{t('non_qualified')}</p>
                  <div style={arrayStyle}>
                    {nonQualifiedAssigns.map((a) => (
                      <Box sx={{ position: 'relative' }} key={a.security.id}>
                        {a.isLeader && (
                          <span style={leaderStyle}>{t('leader')}</span>
                        )}
                        <div style={badgeStyle}>
                          {a.security.lastName} {a.security.firstName}
                          {a.loanInfo?.lendCompanyId && (
                            <>
                              {'/'}
                              {a.loanInfo.lendCompanyName}/
                              {a.loanInfo.lendCompanyBranchName}
                            </>
                          )}
                        </div>
                      </Box>
                    ))}
                    {Array.from({ length: nonQualifiedPendingCount }).map(
                      (_, i) => (
                        <div key={`nonq-pending-${i}`} style={pendingStyle}>
                          {t('pending')}
                        </div>
                      ),
                    )}
                  </div>
                  {nonQualifiedAllianceAssigns.length > 0 && (
                    <div style={allianceContainerStyle}>
                      <p style={allianceTextStyle}>{t('alliance_company')}</p>
                      <div style={arrayStyle}>
                        {nonQualifiedAllianceAssigns.map((a) => (
                          <div key={a.clientId} style={allianceBadgeStyle}>
                            {a.client.displayName}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <p style={hasQualifiedStyle}>{t('qualified')}</p>
                  <div style={arrayStyle}>
                    {qualifiedAssigns.map((a) => (
                      <Box sx={{ position: 'relative' }} key={a.security.id}>
                        {a.isLeader && (
                          <span style={leaderStyle}>{t('leader')}</span>
                        )}
                        <div style={badgeStyle}>
                          {a.security.lastName} {a.security.firstName}
                          {a.loanInfo?.lendCompanyId && (
                            <>
                              {'/'}
                              {a.loanInfo.lendCompanyName}/
                              {a.loanInfo.lendCompanyBranchName}
                            </>
                          )}
                        </div>
                      </Box>
                    ))}
                    {Array.from({ length: qualifiedPendingCount }).map(
                      (_, i) => (
                        <div key={`q-pending-${i}`} style={pendingStyle}>
                          {t('pending')}
                        </div>
                      ),
                    )}
                  </div>
                  {qualifiedAllianceAssigns.length > 0 && (
                    <div style={allianceContainerStyle}>
                      <p style={allianceTextStyle}>{t('alliance_company')}</p>
                      <div style={arrayStyle}>
                        {qualifiedAllianceAssigns.map((a) => (
                          <div key={a.clientId} style={allianceBadgeStyle}>
                            {a.client.displayName}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {order.commentToSecurity && (
              <div style={columnDivStyle}>
                <p style={commentLabelStyle}>{t('comment_to_security')}</p>
                <p style={commentTextStyle}>{order.commentToSecurity}</p>
              </div>
            )}

            <div>
              {/* 関連資料 */}
              <NotificationFilesComponent files={notificationFiles} />
            </div>
          </>
        </Box>
      </Box>
    </Box>
  )
}

// Styles
const cardStyle: CSSProperties = {
  width: '90%',
  margin: '20px auto',
  padding: '20px',
  borderRadius: '10px',
}

const columnDivStyle = {
  display: 'grid',
  gridTemplateColumns: '30% 70%',
  gap: '10px',
  marginBottom: '10px',
  borderBottom: '1px dotted #ccc',
  paddingBottom: '10px',
  wordBreak: 'break-word' as const,
}

const hasQualifiedStyle = {
  marginBottom: '20px',
  fontSize: '10px',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
}

const allianceTextStyle = {
  fontSize: '8px',
  color: 'gray',
  whiteSpace: 'nowrap',
}

const arrayStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  rowGap: '10px',
}

const badgeStyle = {
  backgroundColor: '#f5f5f5',
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '12px',
  wordBreak: 'break-word' as const,
}

const allianceBadgeStyle = {
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '12px',
  border: '0.5px solid #ccc',
}

const pendingStyle = {
  ...badgeStyle,
  backgroundColor: '#eee',
  color: '#aaa',
}

const leaderStyle: CSSProperties = {
  position: 'absolute',
  top: '-15px',
  left: '-5px',
  fontSize: '13px',
  color: PRIMARY_COLOR,
  border: '1px solid',
  padding: '0 5px',
  borderRadius: '5px',
  backgroundColor: 'white',
  whiteSpace: 'nowrap',
}

const dateHeaderStyle: CSSProperties = {
  textAlign: 'left',
  marginBottom: '10px',
  borderBottom: '1px dotted #ccc',
  paddingBottom: '10px',
}

const dateBoldTextStyle: CSSProperties = {
  fontWeight: 'bold',
}

const loanInfoBadgeStyle: CSSProperties = {
  margin: '5px 0',
  padding: '0px 5px',
  backgroundColor: BLUE_COLOR,
  display: 'inline-block',
  color: 'white',
  wordBreak: 'break-word',
}

const labelTextStyle: CSSProperties = {
  fontWeight: 'bold',
  fontSize: '12px',
}

const mapButtonContainerStyle: CSSProperties = {
  marginTop: '10px',
  textAlign: 'left',
}

const workersContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}

const allianceContainerStyle: CSSProperties = {
  marginTop: '10px',
}

const commentLabelStyle: CSSProperties = {
  fontWeight: 'bold',
  fontSize: '12px',
  whiteSpace: 'nowrap',
}

const commentTextStyle: CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  marginTop: '25px',
}
